import {
  store,
  getContext,
  getElement,
  getServerState,
  useLayoutEffect,
  useEffect,
} from '@wordpress/interactivity';
import DOMPurify from 'dompurify';
import { format, register } from 'timeago.js';

/**
 * The Interactivity API store for MediaPress Live blocks
 *
 * This is currently built as part of the live feed block, but it's intended to be used
 * across all live blocks so could be built/enqueued as a separate script.
 *
 * When this is on a page, it'll poll the server to check for new entries and handle
 * fetching data and updating the state accordingly.
 *
 * This store can be extended further in other blocks, if additional state or actions are needed.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-interactivity/
 */
const { state, actions } = store('mediapress-live', {
  state: {
    get hasEntries() {
      return state.entries?.length > 0;
    },
    get hasPinnedEntry() {
      return !!state.pinnedEntry;
    },
    get isPinnedEntry() {
      const context = getContext();
      return context?.entry?.id === state.pinnedEntry?.id;
    },
    get isLive() {
      return state.parent?.meta.mediapress_live_is_live;
    },
    shouldQueue: false,
    queuedSubscriptions: {},
    get queuedSubscriptionsLength() {
      return Object.keys(state.queuedSubscriptions).length;
    },
    get hasPrevPage() {
      return state.currentPage > 1;
    },
    get hasNextPage() {
      return state.currentPage < state.totalPages;
    },
    get prevPageUrl() {
      if (!state.hasPrevPage) {
        return null;
      }
      const url = new URL(window.location.href);
      const prevPage = state.currentPage - 1;
      url.searchParams.set('live-feed-page', prevPage);
      return url.toString();
    },
    get nextPageUrl() {
      const url = new URL(window.location.href);
      url.searchParams.set('live-feed-page', state.currentPage + 1);
      return url.toString();
    },
  },
  actions: {
    /**
     * Subscribe to a post and store the subscriptions in state
     *
     * @return {Promise<void>}
     */
    subscribe: async () => {
      const currentSubscriptions = await fetch(
        `${state.apiRoot}/${state.restBase}/${state.postId}/subscribe`,
        {
          headers: {
            'X-WP-Nonce': window.mediaPressLiveRestApi?.nonce,
          },
        },
      ).then((response) => response.json());

      if (!currentSubscriptions) {
        return;
      }

      /**
       * Loop over the latest subscriptions and check if they are already in the state
       */
      Object.entries(currentSubscriptions).forEach((subscription) =>
        actions.checkSubscription(subscription),
      );

      if (!state.subscriptions) {
        return;
      }

      /**
       * Loop over the subscriptions already in state and check if any have been removed
       */
      Object.entries(state.subscriptions).forEach((subscription) =>
        actions.checkRemove(subscription, currentSubscriptions),
      );
    },
    /**
     * Check if the entity should be removed from the state
     *
     * @param {Subscription}        subscription        The subscription to check
     * @param {Array<Subscription>} currentSubscriptions The latest subscriptions
     */
    checkRemove: (subscription, currentSubscriptions) => {
      const [id, data] = subscription;
      const exists = currentSubscriptions[id];

      if (!exists) {
        // console.log('Deleted entry:', id);
        delete state.subscriptions[id];
        actions.removeEntity(id, data);
      }
    },
    /**
     * Check if the entity is already in the state
     *
     * @param {Subscription} subscription The subscription
     */
    checkSubscription: (subscription) => {
      const [id, data] = subscription;

      const currentSubscription = state.subscriptions[id];

      if (!currentSubscription) {
        // console.log('New entry:', subscription);
        if (state.shouldQueue || state.queuedSubscriptionsLength > 0) {
          state.queuedSubscriptions[id] = data;
        } else {
          state.subscriptions[id] = data;
          actions.getEntity(id, data);
        }
        return;
      }

      if (currentSubscription.modified !== data.modified) {
        state.subscriptions[id] = data;
        actions.getEntity(id, data);
      }
    },
    /**
     * Get the entity data from the server
     *
     * @param  {number}           id           The subscription ID
     * @param  {SubscriptionData} subscription The subscription data
     *
     * @return {Promise<void>}
     */
    getEntity: async (id, { modified, restBase, entityType }) => {
      /** @type {WPPost} */
      const entity = await fetch(`${state.apiRoot}/${restBase}/${id}/modified/${modified}`).then(
        (response) => response.json(),
      );

      if (!entity.id) {
        return;
      }

      actions.addOrUpdateEntity(entity, entityType);
    },
    /**
     * Add or update an entity in the state
     *
     * The method of doing so is different based on whether
     * the entity state stores an array or a single entity
     *
     * @param {WPPost} entity     The entity data
     * @param {string} entityType The entity type
     */
    addOrUpdateEntity: (entity, entityType) => {
      let idx = -1;

      if (Array.isArray(state[entityType])) {
        idx = state[entityType].findIndex((existingEntity) => existingEntity.id === entity.id);
      } else {
        idx = state[entityType]?.id === entity.id ? null : -1;
      }

      if (idx === -1) {
        actions.addEntity(entity, entityType);
        return;
      }

      actions.updateEntity(entity, entityType, idx);
    },
    /**
     * Remove the entity data from the state
     *
     * @param {number}           id   The ID of the entity to remove
     * @param {SubscriptionData} data The subscription data
     */
    removeEntity: (id, data) => {
      const idx = state[data.entityType]?.findIndex((entity) => String(entity.id) === String(id));

      if (idx !== -1) {
        // console.log('removing entity', id, data.entityType, idx);
        state[data.entityType].splice(idx, 1);
      }
    },
    /**
     * Add the entity data to the state
     *
     * @param {WPPost} entity     The entity data
     * @param {string} entityType The entity type
     */
    addEntity: (entity, entityType) => {
      // If the entity is not an array, set it as the entity
      if (!Array.isArray(state[entityType])) {
        state[entityType] = entity;
        return;
      }

      // If array is empty, just push the entity
      if (!state[entityType].length) {
        state[entityType].push(entity);
        return;
      }

      // Find the correct insertion point based on date
      const insertIndex = state[entityType].findIndex(
        (existingEntity) => new Date(existingEntity.date) <= new Date(entity.date),
      );

      // Insert the entity at the correct index, avoid mutating the original array
      const newEntityState = [
        ...state[entityType].slice(0, insertIndex),
        entity,
        ...state[entityType].slice(insertIndex),
      ];

      state[entityType] = newEntityState;
    },
    /**
     * Update the entity data in the state
     *
     * @param {WPPost} entity     The entity data
     * @param {string} entityType The entity type
     * @param {number|null} idx   The index of the entity in the state
     */
    updateEntity: (entity, entityType, idx) => {
      // if there's no index, update the entity on state object, not an array
      if (idx === null) {
        state[entityType] = entity;
        return;
      }

      // otherwise, update the entry in it's array
      state[entityType][idx] = entity;
    },
    /**
     * Load any pending subscriptions and add them to the state
     */
    loadQueuedSubscriptions: () => {
      Object.entries(state.queuedSubscriptions).forEach(([id, data]) => {
        actions.getEntity(id, data);
        state.subscriptions[id] = data;
        delete state.queuedSubscriptions[id];
      });

      const liveFeedList = document.querySelector('.live-feed-list');
      liveFeedList.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    *navigate(event) {
      event.preventDefault();
      const { actions: router } = yield import('@wordpress/interactivity-router');
      yield router.navigate(event.target.href, { force: true });
    },
  },
  callbacks: {
    /**
     * Update the entries when server state changes
     * This should be used in a `data-wp-watch` directive
     * to ensure entries reload when interactivity-router navigates
     * to a new page.
     *
     * @see https://developer.wordpress.org/block-editor/reference-guides/interactivity-api/core-concepts/undestanding-global-state-local-context-and-derived-state/#getserverstate
     */
    updateServerEntries() {
      const serverState = getServerState();
      state.entries = serverState.entries;
      state.currentPage = serverState.currentPage;
    },
    /**
     * Convert a timestamp to time ago (e.g. 5 minutes ago)
     */
    timeAgo: () => {
      const context = getContext();
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useLayoutEffect(() => {
        const gmtTime = context?.entry?.date_gmt;

        if (!gmtTime) {
          return;
        }

        /**
         * WordPress stores the UTC time in the date_gmt field, but it doesn't always end with 'Z' - meaning timeago.js won't parse it correctly.
         */
        const timeStamp = gmtTime.endsWith('Z') ? gmtTime : `${gmtTime}Z`;

        if (!timeStamp) {
          return;
        }

        const { ref } = getElement();

        updateTime();

        const interval = setInterval(updateTime, 60000); // Update every minute

        function updateTime() {
          ref.innerHTML = DOMPurify.sanitize(format(timeStamp, state.locale));
        }

        return () => clearInterval(interval);
      }, [context?.entry?.date_gmt]);
    },
    /**
     * Check if we should queue the subscriptions based on whether
     * the user is scrolled to the top of the feed or not
     */
    checkShouldQueue: () => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useEffect(() => {
        const { ref } = getElement();
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.boundingClientRect.top > 0) {
                state.shouldQueue = false;
              } else {
                state.shouldQueue = true;
              }
            });
          },
          { threshold: 0.1 },
        );
        // observe the element
        if (ref) {
          observer.observe(ref);
        }
        // unobserve the element when the component is unmounted
        return () => {
          if (ref) {
            observer.unobserve(ref);
          }
        };
      }, []);
    },
    /**
     * Poll for updates to the live feed
     */
    pollForUpdates: () => {
      const interval = setInterval(() => {
        if (state.currentPage === 1 && state.postId) {
          actions.subscribe();
        }
      }, 1500);

      return () => clearInterval(interval);
    },
  },
});

/**
 * Defining our supported locales ensures that webpack
 * will handle the dynamic import correctly, and also
 * allows to to know which locales have been tested.
 *
 * @see https://github.com/hustcc/timeago.js/tree/37c19a8d11c529ea9d6c9b9feee7745eb9b2ccef/src/lang
 */
const SUPPORTED_LOCALES = {
  ar: () => import('timeago.js/lib/lang/ar'),
};

if (state.locale && SUPPORTED_LOCALES[state.locale]) {
  SUPPORTED_LOCALES[state.locale]()
    .then((module) => {
      register(state.locale, module.default);
    })
    .catch((error) => {
      // eslint-disable-next-line no-console -- Log the error to the console
      console.error(`Failed to load timeago.js locale: ${state.locale}`, error);
    });
}
